import { useCallback, useEffect, useRef, useState } from 'react';
import { TEAM_CHANGE_EVENT, fetchLoginUsers, fetchTeam, getTeamSnapshot, refreshTeamFromApi, resetTeamCache } from '../../services/TeamService';
import type { LoginUser, TeamLifecycle, TeamMember } from '../../types/Team';

export interface TeamData {
  /** The people of the list on screen (active, archived or trash). */
  shown: TeamMember[];
  /** The active roster: used for the cards and to pair logins with profiles. */
  active: TeamMember[];
  logins: LoginUser[];
  loading: boolean;
  /** The roster failed to load. */
  loadError: unknown;
  /** The logins failed to load (the roster is still shown). */
  loginsError: unknown;
  refresh: () => Promise<void>;
}

/**
 * Loads the roster for the chosen list and the workspace logins for the active
 * project, mirrors the active roster in the shared cache, and starts over when
 * the project or the list changes. A stale answer from a previous project or
 * list is dropped.
 */
export function useTeamData(projectId: string | null, lifecycle: TeamLifecycle, enabled: boolean): TeamData {
  const [shown, setShown] = useState<TeamMember[]>([]);
  const [active, setActive] = useState<TeamMember[]>([]);
  const [logins, setLogins] = useState<LoginUser[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [loginsError, setLoginsError] = useState<unknown>(null);
  const ticket = useRef(0);
  // What the arrays below were loaded for. Anything else is not shown, so a project
  // or list switch never paints one frame of the previous one.
  const [loadedFor, setLoadedFor] = useState('');
  const key = `${projectId ?? ''}|${lifecycle}`;

  const refresh = useCallback(async () => {
    if (!enabled) { setLoading(false); return; }
    const mine = ++ticket.current;
    setLoading(true);
    const [roster, listed, people] = await Promise.allSettled([
      refreshTeamFromApi(),
      lifecycle === 'active' ? Promise.resolve(null) : fetchTeam(lifecycle),
      fetchLoginUsers(),
    ]);
    if (mine !== ticket.current) return;
    setLoadedFor(`${projectId ?? ''}|${lifecycle}`);
    if (roster.status === 'fulfilled') setActive(roster.value);
    const listFailure = lifecycle === 'active' ? (roster.status === 'rejected' ? roster.reason : null) : (listed.status === 'rejected' ? listed.reason : null);
    setLoadError(listFailure);
    if (!listFailure) {
      setShown(lifecycle === 'active' ? (roster.status === 'fulfilled' ? roster.value : []) : (listed.status === 'fulfilled' ? listed.value ?? [] : []));
    }
    if (people.status === 'fulfilled') { setLogins(people.value); setLoginsError(null); } else setLoginsError(people.reason);
    setLoading(false);
  }, [enabled, lifecycle, projectId]);

  // A new project or list: forget what the old one showed, then load.
  useEffect(() => {
    if (projectId) resetTeamCache();
    setShown([]); setActive([]); setLogins([]); setLoadError(null); setLoginsError(null); setLoadedFor('');
    void refresh();
    return () => { ticket.current += 1; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, lifecycle, enabled]);

  // Other parts of the app (the form, background sync) refresh the shared cache.
  useEffect(() => {
    if (lifecycle !== 'active') return undefined;
    const sync = (e: Event) => {
      // Only this project's roster; a snapshot announced for another project is ignored.
      const announced = (e as CustomEvent<string | null>).detail;
      if (announced && projectId && announced !== projectId) return;
      const latest = getTeamSnapshot(); setActive(latest); setShown(latest); };
    window.addEventListener(TEAM_CHANGE_EVENT, sync);
    return () => window.removeEventListener(TEAM_CHANGE_EVENT, sync);
  }, [lifecycle, projectId]);

  const current = loadedFor === key;
  return {
    shown: current ? shown : [], active: current ? active : [], logins: current ? logins : [],
    loading, loadError: current ? loadError : null, loginsError: current ? loginsError : null, refresh,
  };
}
